import message from 'antd/es/message';
import { useInfiniteQuery, InfiniteData, useQuery } from 'react-query';
import produce from 'immer';
import { useCallback } from 'react';

import { request, queryClient, toastError } from '@/utils';
import { NftToken } from '@/views/NFT';

import { TokenHistory, ContentScope } from '../types';

const PAGE_COUNT = 20;

export const useTokenHistory = (
  scope: ContentScope,
  id: string,
  chain: string,
  token_id?: string,
) => {
  const url = scope === 'bundles' ? '/bundle/history_list' : '/history/list';

  const { data, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage } = useInfiniteQuery(
    [url, chain, id, token_id],
    async ({ pageParam = 0 }) => {
      const params: Record<string, string | number> = {
        [scope === 'bundles' ? 'id' : 'user_addr']: id,
        chain,
        start_time: pageParam,
        page_count: PAGE_COUNT,
      };

      if (token_id) {
        params.token_id = token_id;
      }

      return request[scope === 'bundles' ? 'async' : 'get']<TokenHistory>(url, params).then(res => {
        res.history_list.sort((v1, v2) => v2.time_at - v1.time_at);
        return res;
      });
    },
    {
      staleTime: 0,
      cacheTime: 0,
      getNextPageParam: lastPage => {
        const length = lastPage.history_list?.length;
        if (length >= PAGE_COUNT) {
          return lastPage.history_list![length - 1].time_at;
        }
        // Return false means no next page
        return false;
      },
      onError(error: any) {
        toastError(error, 'failed to fetch, please try later');
      },
    },
  );

  return {
    data: data?.pages,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  };
};

export const updateHistoryNftInfo = (scope: ContentScope, data?: NftToken) => {
  if (!data?.id) {
    return;
  }

  queryClient.setQueriesData(
    [scope === 'bundles' ? '/bundle/history_list' : '/history/list'],
    (pre?: InfiniteData<TokenHistory>) => {
      if (!pre) {
        return pre!;
      }

      return produce(pre, draft => {
        // 使用 find 提前退出循环
        draft.pages.find(x => {
          if (x?.token_dict?.[data.id]) {
            Object.assign(x.token_dict[data.id], data);

            return true;
          }

          return false;
        });

        return draft;
      });
    },
  );

  message.success('Metadata updated');
};

export const useAllHistoryList = (id: string, timeRange?: number[]) => {
  const url = '/history/all_list';
  const select = useCallback(data => {
    return {
      ...data,
      token_dict: data.token_uuid_dict,
    } as TokenHistory;
  }, []);

  const query = useQuery(
    [url, id, timeRange],
    ({ signal }) =>
      request.async<TokenHistory>(
        url,
        {
          user_id: id,
          // 注意，是反过来传的
          start_time: timeRange?.[1],
          end_time: timeRange?.[0],
        },
        {
          signal,
          timeout: 2 * 60 * 1000,
        },
      ),
    {
      enabled: !!id,
      cacheTime: 2 * 60 * 1000,
      select,
      onError(err: any) {
        toastError(err, 'enter analysis mode failed');
      },
    },
  );

  return query;
};
